import { Button, Checkbox, Heading, List, Spinner, Stack, Text } from '@chakra-ui/react'
import type {
  ClientCredentialsResponse,
  PublicJoinResponse,
  RegistrationStatus,
  StartAttemptResponse,
} from '@ipc/shared'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { api, isApiError } from '~/api/client'
import { ActionBar } from '~/components/member/ActionBar'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { useOpenFaq } from '~/components/member/WhyLink'
import { useT } from '~/i18n/use-t'
import { EMBEDDED, type EmbeddedEntry } from '../providers'
import type { FaceDone } from '../types'
import { Wait } from '../Wait'

type Embedded = Extract<StartAttemptResponse, { kind: 'embedded' }>
type Phase =
  | { name: 'question' }
  | { name: 'starting' }
  | { name: 'running'; start: Embedded }
  | { name: 'finishing' }
  | { name: 'camera' }
  | { name: 'unsupported'; registration: RegistrationStatus | null }
  | { name: 'error'; error: unknown }

const FINISHING_KEY = ['member', 'registration', 'finishing'] as const

/** SDK error states that mean "no camera picture", before anything was streamed. */
const CAMERA_STATES = new Set(['CAMERA_ACCESS_ERROR', 'DEFAULT_CAMERA_NOT_FOUND_ERROR'])

/**
 * The embedded-SDK flow (copy v8 "S4 check inside our page"): the photosensitivity question when the provider says
 * so, then the attempt, then the provider's lazy component. The host makes every call to the API; the component
 * only drives the provider's SDK. Nothing here names a provider except the registry lookup by id.
 */
export function EmbeddedHost({
  join,
  onDone,
  registry = EMBEDDED,
  member = false,
}: {
  join: PublicJoinResponse
  onDone: (done: FaceDone) => void
  registry?: Record<string, EmbeddedEntry>
  /**
   * A member (R32 recheck) runs the check: the registration status is not theirs to read (it needs the registration
   * cookie), so a finish the page cannot tell hands over to the member page, which polls the member's own card.
   */
  member?: boolean
}) {
  const { t, config } = useT()
  const friendly = useFriendlyError()
  const openFaq = useOpenFaq()
  const entry = registry[join.providerId]
  const asks = entry?.asksPhotosensitivity(join.publicSettings) ?? false
  const [phase, setPhase] = useState<Phase>(asks ? { name: 'question' } : { name: 'starting' })
  const credentials = useRef<Promise<ClientCredentialsResponse['credentials']> | null>(null)
  const started = useRef(false)
  const lastAnswer = useRef<boolean | undefined>(undefined)
  const [flashOk, setFlashOk] = useState(true)

  const start = useCallback(async (noFlash?: boolean) => {
    lastAnswer.current = noFlash
    setPhase({ name: 'starting' })
    credentials.current = null
    try {
      const res = await api('attemptStart', {
        body: { consentBiometric: true, ...(noFlash === undefined ? {} : { noFlash }) },
      })
      if (res.kind !== 'embedded') throw new Error('unexpected_flow')
      setPhase({ name: 'running', start: res })
    } catch (error) {
      setPhase({ name: 'error', error })
    }
  }, [])

  useEffect(() => {
    void entry?.preload()
  }, [entry])
  useEffect(() => {
    if (started.current || !entry || asks) return
    started.current = true
    void start()
  }, [entry, asks, start])

  if (!entry) return <ErrorAlert>{t('face.unknownFlow')}</ErrorAlert>

  const attemptId = phase.name === 'running' ? phase.start.attemptId : null
  const getCredentials = () => {
    if (!attemptId) return Promise.reject(new Error('no_attempt'))
    credentials.current ??= api('attemptCredentials', { params: { id: attemptId } }).then((r) => r.credentials)
    return credentials.current
  }
  const signal = async (path: 'attemptComplete' | 'attemptClientError', state?: string) => {
    if (!attemptId) return null
    try {
      return path === 'attemptComplete'
        ? await api('attemptComplete', { params: { id: attemptId } })
        : await api('attemptClientError', { params: { id: attemptId }, body: { state: state ?? 'UNKNOWN' } })
    } catch {
      return null
    }
  }

  const onComplete = async () => {
    await signal('attemptComplete')
    onDone({ to: 'wait' })
  }
  const onClientError = async (state: string, streaming: boolean) => {
    const res = await signal('attemptClientError', state)
    if (!streaming && CAMERA_STATES.has(state)) return setPhase({ name: 'camera' })
    if (res?.state === 'start_failed') {
      const reg = member ? null : await api('registrationStatus').catch(() => null)
      return setPhase({ name: 'unsupported', registration: reg })
    }
    if (member) return onDone({ to: 'wait' })
    setPhase({ name: 'finishing' })
  }
  const onCancel = async (streaming: boolean) => {
    if (!streaming) return onDone({ to: 'face', stopped: true })
    await signal('attemptClientError', 'USER_CANCEL')
    if (member) return onDone({ to: 'wait' })
    setPhase({ name: 'finishing' })
  }

  const switchTo = async (o: { id: string; joinPath: string }) => {
    try {
      await api('registrationProvider', { body: { provider: o.id } })
      onDone({ to: 'switch', joinPath: o.joinPath })
    } catch (error) {
      setPhase({ name: 'error', error })
    }
  }

  switch (phase.name) {
    case 'question':
      // One tick, on by default (owner, 2026-10-30): most people are fine with the flashes; unticking it chooses the
      // check without them. Either way Continue starts the check.
      return (
        <Stack gap={4}>
          <Heading as='h2' size='xl'>
            {t('embedded.flashTitle')}
          </Heading>
          <Text>{t('embedded.flashText')}</Text>
          <Checkbox.Root
            checked={flashOk}
            onCheckedChange={(e) => setFlashOk(e.checked === true)}
            alignItems='flex-start'
            gap={3}
            cursor='pointer'
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control boxSize='5' mt='0.5' />
            <Checkbox.Label fontSize='md' lineHeight='1.5'>
              {t('embedded.flashOk')}
            </Checkbox.Label>
          </Checkbox.Root>
          <ActionBar>
            <Button size='lg' w='full' onClick={() => void start(!flashOk)}>
              {t('common.continue')}
            </Button>
          </ActionBar>
        </Stack>
      )
    case 'starting':
      return (
        <Stack gap={3} role='status'>
          <Spinner aria-hidden />
          <Text>{t('embedded.loading')}</Text>
        </Stack>
      )
    case 'running':
      return (
        <Suspense
          fallback={
            <Stack gap={3} role='status'>
              <Spinner aria-hidden />
              <Text>{t('embedded.loading')}</Text>
            </Stack>
          }
        >
          <entry.Component
            start={phase.start}
            getCredentials={getCredentials}
            onComplete={() => void onComplete()}
            onClientError={(s, streaming) => void onClientError(s, streaming)}
            onCancel={(streaming) => void onCancel(streaming)}
            member={member}
            t={(k, v) => t(k, v)}
          />
        </Suspense>
      )
    case 'finishing':
      return (
        <Wait
          queryKey={FINISHING_KEY}
          finishing={join.displayName}
          onDecided={(r) => onDone({ to: 'registration', registration: r })}
        />
      )
    case 'camera':
      return (
        <Stack gap={3} align='flex-start' role='alert'>
          <Text fontWeight='bold'>{t('capture.noCamera')}</Text>
          <Button onClick={() => void start(lastAnswer.current)}>{t('common.tryAgain')}</Button>
          <Text>{t('capture.noCameraBlock')}</Text>
          <Button variant='outline' onClick={() => openFaq('cameraAllow')}>
            {t('capture.noCameraHow')}
          </Button>
          <Text>{t('capture.noCameraOther')}</Text>
        </Stack>
      )
    case 'unsupported': {
      const reg = phase.registration
      const others = reg?.canSwitchProvider ? reg.otherProviders : []
      return (
        <Stack gap={4} role='alert'>
          <Heading as='h2' size='xl'>
            {t('embedded.notWorkTitle')}
          </Heading>
          {others.length > 0 ? (
            <>
              <Text>{t('embedded.notWorkText')}</Text>
              <List.Root ps={6} gap={4}>
                {others.map((o) => (
                  <List.Item key={o.id}>
                    {t('embedded.notWorkSwitch', { org: config.org.name, other: o.displayName })}
                    <Button mt={2} display='block' onClick={() => void switchTo(o)}>
                      {t('embedded.notWorkUse', { other: o.displayName })}
                    </Button>
                  </List.Item>
                ))}
                <List.Item>{t('embedded.notWorkEmail')}</List.Item>
              </List.Root>
            </>
          ) : (
            <Text>{t('embedded.notWorkEmailOnly')}</Text>
          )}
        </Stack>
      )
    }
    case 'error':
      return (
        <Stack gap={3} align='flex-start'>
          <ErrorAlert>{friendly(phase.error)}</ErrorAlert>
          {!isApiError(phase.error, 'manual_route') && (
            <Button onClick={() => void start(lastAnswer.current)}>{t('common.tryAgain')}</Button>
          )}
        </Stack>
      )
  }
}
